// src/admin/pages/VolatilityControl.tsx — Per-Table Volatility Management
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import useAdmin from '../hooks/useAdmin';
import { Loader2, Zap, TrendingUp, TrendingDown } from 'lucide-react';
import { fetchCsrfToken } from '../../shared/api';

interface VolatilityProfile {
  tier: string;
  label: string;
  description: string;
  smallBoost: number;
  bossWeight: number;
  hpMult: number;
  varianceMult: number;
  spawnRate: number;
}

interface TableRow {
  id: string;
  name: string;
  manager_email?: string;
  volatility_tier?: string;
  current_rtp: number;
}

const TIER_COLORS: Record<string, string> = {
  LOW:    'bg-green-100 text-green-800 border-green-300',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  HIGH:   'bg-red-100 text-red-800 border-red-300',
};
const TIER_ICONS: Record<string, React.ReactNode> = {
  LOW:    <TrendingDown size={14} className="inline mr-1" />,
  MEDIUM: <Zap size={14} className="inline mr-1" />,
  HIGH:   <TrendingUp size={14} className="inline mr-1" />,
};

export default function VolatilityControl() {
  const { token, error, setError } = useAdmin();
  const [tables, setTables] = useState<TableRow[]>([]);
  const [profiles, setProfiles] = useState<VolatilityProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [tablesRes, profilesRes] = await Promise.all([
        fetch('/admin/tables', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
        fetch('/admin/volatility-profiles', { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
      ]);
      const tablesData   = await tablesRes.json();
      const profilesData = await profilesRes.json();
      setTables(Array.isArray(tablesData) ? tablesData : []);
      setProfiles(Array.isArray(profilesData) ? profilesData : []);
      const init: Record<string, string> = {};
      (Array.isArray(tablesData) ? tablesData : []).forEach((t: TableRow) => {
        init[t.id] = t.volatility_tier || 'MEDIUM';
      });
      setSelected(init);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (tableId: string) => {
    const tier = selected[tableId];
    if (!tier) return;
    setSaving(tableId);
    try {
      const csrfToken = await fetchCsrfToken().catch(() => '');
      const res = await fetch(`/admin/tables/${tableId}/volatility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({ tier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Failed');
      toast.success(`Volatility set to ${tier}`);
      setTables(prev => prev.map(t => t.id === tableId ? { ...t, volatility_tier: tier } : t));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Volatility Control</h2>
        <p className="text-gray-500 mt-1">Set per-table volatility — LOW (frequent small wins) → HIGH (rare big wins)</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="font-bold">✕</button>
        </div>
      )}

      {/* Profile Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {profiles.map(p => (
          <div key={p.tier} className={`border-2 rounded-xl p-4 ${TIER_COLORS[p.tier]}`}>
            <div className="flex items-center gap-2 mb-2">
              {TIER_ICONS[p.tier]}
              <h3 className="font-bold text-lg">{p.tier} — {p.label}</h3>
            </div>
            <p className="text-sm mb-3">{p.description}</p>
            <div className="grid grid-cols-2 gap-1 text-xs font-mono">
              <span>Small Fish Boost: {p.smallBoost}x</span>
              <span>Boss Weight: {p.bossWeight}x</span>
              <span>HP Mult: {p.hpMult}x</span>
              <span>Variance: {p.varianceMult}x</span>
              <span>Spawn Rate: {(p.spawnRate * 100).toFixed(0)}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tables List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-indigo-600" size={40} />
        </div>
      ) : (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">All Tables</h3>
          </div>
          <table className="w-full">
            <thead className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <tr>
                <th className="p-4 text-left">Table Name</th>
                <th className="p-4 text-left">Manager</th>
                <th className="p-4 text-center">Current RTP</th>
                <th className="p-4 text-center">Current Volatility</th>
                <th className="p-4 text-center">Set Volatility</th>
                <th className="p-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {tables.map(t => (
                <tr key={t.id} className="border-b hover:bg-gray-50">
                  <td className="p-4 font-medium">{t.name}</td>
                  <td className="p-4 text-gray-500 text-sm">{t.manager_email || '—'}</td>
                  <td className="p-4 text-center font-bold text-indigo-700">
                    {(Number(t.current_rtp) * 100).toFixed(1)}%
                  </td>
                  <td className="p-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${TIER_COLORS[t.volatility_tier || 'MEDIUM']}`}>
                      {TIER_ICONS[t.volatility_tier || 'MEDIUM']}
                      {t.volatility_tier || 'MEDIUM'}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <select
                      value={selected[t.id] || 'MEDIUM'}
                      onChange={e => setSelected(prev => ({ ...prev, [t.id]: e.target.value }))}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="LOW">LOW</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="HIGH">HIGH</option>
                    </select>
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => handleSave(t.id)}
                      disabled={saving === t.id || selected[t.id] === (t.volatility_tier || 'MEDIUM')}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {saving === t.id ? <Loader2 className="animate-spin" size={16} /> : 'Apply'}
                    </button>
                  </td>
                </tr>
              ))}
              {tables.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-gray-400">No tables found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
